// actions/videoSettingsAction.js
import axios from 'axios';
// NOTE: Assuming VIDEO_ACTION_TYPES and baseURL are defined in this relative path or an environment config
import { VIDEO_ACTION_TYPES, baseURL } from './types'; 

// ==================== AUTH & UTILITY FUNCTIONS (Copied/Adapted from paymentSettingActions) ====================

// Utility: Get token from localStorage
const getAuthToken = () => {
  try {
    const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
    const directToken = localStorage.getItem('token');
    return loginData.token || directToken || null;
  } catch (error) {
    console.error('Error retrieving auth token:', error);
    return null;
  }
};

// Get auth headers configuration
const getAuthHeaders = () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  };
};

// Utility to ensure URL is correctly formatted
const normalizeUrl = (base, path) => {
    const normalizedBase = base.endsWith('/') ? base : base + '/';
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    
    return normalizedBase + normalizedPath;
};


// Enhanced error handler (from paymentSettingActions.js)
const handleApiError = (error, dispatch, failType, shouldReject = true) => { 
  console.error('API Error:', error);
  
  let errorMessage = 'An unexpected error occurred';
  
  if (error.response) {
    if (error.response.status === 404) {
      errorMessage = 'API endpoint not found. Please check if the backend service is running.';
    } else if (error.response.status === 401) {
      errorMessage = 'Authentication failed. Please login again.';
    } else if (error.response.status === 500) {
      errorMessage = error.response.data?.message || 
                    error.response.data?.error || 
                    'Server error. Please try again later.';
    } else {
      errorMessage = error.response.data?.message || 
                    error.response.data?.error || 
                    `Server error: ${error.response.status}`;
    }
  } else if (error.request) {
    errorMessage = 'No response from server. Please check your connection.';
  } else {
    errorMessage = error.message;
  }

  dispatch({
    type: failType,
    payload: errorMessage,
  });
  
  if (shouldReject) {
      return Promise.reject(new Error(errorMessage));
  }
  return new Error(errorMessage); 
};

// Generic API action creator for POST/PATCH (from paymentSettingActions.js)
const createApiAction = (types, path, method = 'post', data = null) => async (dispatch) => {
  try {
    dispatch({ type: types.REQUEST });
    
    const url = normalizeUrl(baseURL, path); 

    const config = getAuthHeaders();
    let response;

    switch (method.toLowerCase()) {
      case 'post':
        response = await axios.post(url, data, config);
        break;
      case 'patch':
        response = await axios.patch(url, data, config);
        break;
      default:
        throw new Error('Unsupported method for createApiAction');
    }

    const payload = response.data.success === 1 ? (response.data.data || response.data) : response.data;

    dispatch({
      type: types.SUCCESS,
      payload,
    });
    return Promise.resolve(payload);

  } catch (error) {
    return handleApiError(error, dispatch, types.FAIL, true); 
  }
};

// Generic GET action with graceful error handling (from paymentSettingActions.js)
const createGetAllAction = (types, path) => async (dispatch, getState) => {
  try {
    dispatch({ type: types.REQUEST });

    const url = normalizeUrl(baseURL, path); 

    const config = getAuthHeaders();
    // Retrieves hospitalId from Redux or localStorage
    const hospitalId = getState().login?.loginData?.hospitalID || JSON.parse(localStorage.getItem('loginData') || '{}').hospitalID || '0';

    const { data } = await axios.post( 
      url,
      { hospitalID: hospitalId }, // Use 'hospitalID' key to match backend expectation based on video action file
      config
    );

    const settings = data.success === 1 ? (data.data || []) : [];

    dispatch({
      type: types.SUCCESS,
      payload: settings,
    });
    return Promise.resolve(settings);

  } catch (error) {
    // Graceful failure: Sets error but resolves to [] to prevent component crash
    handleApiError(error, dispatch, types.FAIL, false); 
    
    dispatch({
      type: types.SUCCESS,
      payload: [],
    });
    return Promise.resolve([]);
  }
};


// ==================== VIDEO SETTINGS ACTIONS ====================

// NOTE: The hid parameter is kept in the signature for consistency with how the component calls it, 
// but it is ignored inside createGetAllAction which gets it from state/localStorage.

export const createVideoSetting = (videoData) => 
  createApiAction(
    {
      REQUEST: VIDEO_ACTION_TYPES.CREATE_VIDEO_SETTING_REQUEST,
      SUCCESS: VIDEO_ACTION_TYPES.CREATE_VIDEO_SETTING_SUCCESS,
      FAIL: VIDEO_ACTION_TYPES.CREATE_VIDEO_SETTING_FAIL
    },
    'api/appointmentVideoSettings/create',
    'post',
    videoData
  );

export const getAllVideoSettings = (hid) => 
  createGetAllAction(
    {
      REQUEST: VIDEO_ACTION_TYPES.GET_VIDEO_SETTINGS_REQUEST,
      SUCCESS: VIDEO_ACTION_TYPES.GET_VIDEO_SETTINGS_SUCCESS,
      FAIL: VIDEO_ACTION_TYPES.GET_VIDEO_SETTINGS_FAIL
    },
    '/api/appointmentVideoSettings/getall'
  );

export const updateVideoSetting = (videoData) => 
  createApiAction(
    {
      REQUEST: VIDEO_ACTION_TYPES.UPDATE_VIDEO_SETTING_REQUEST,
      SUCCESS: VIDEO_ACTION_TYPES.UPDATE_VIDEO_SETTING_SUCCESS,
      FAIL: VIDEO_ACTION_TYPES.UPDATE_VIDEO_SETTING_FAIL
    },
    'api/appointmentVideoSettings/update',
    'patch',
    videoData
  );

// Reset action (If needed, add this to VIDEO_ACTION_TYPES)
// export const resetVideoSettingState = () => (dispatch) => {
//   dispatch({ type: VIDEO_ACTION_TYPES.RESET_VIDEO_SETTING_STATE });
// };